import { formatNumberAsPercent, formatNumberAsPrice } from '@shared/common'
import { Types } from 'service-api'
import {
  TMembershipBenefit,
  TBenefitConsumptionRecord,
  TMembershipCycle,
  TLocationUserProfileMembership,
} from './types'
import { DEFAULT_TIMEZONE, getTzDateInUtc } from '@shared/dates'
import { membershipCycleForDate, getCycleDurationInDays } from '@shared/memberships'
import { formatInTimeZone } from 'date-fns-tz'
import { add } from 'date-fns'

export const getBenefitDiscount = (benefit: Types.BenefitAdminDto | Types.BenefitClientDto) => {
  if (benefit.priceType === 'RATE') {
    return `${benefit.amount ? formatNumberAsPercent(benefit.amount * 100, { keepZero: false }) : ''}`
  } else {
    return `${benefit.amount ? formatNumberAsPrice(benefit.amount, undefined, undefined, false) : ''}`
  }
}

export const isConsumable = (type?: TMembershipBenefit) => type === 'CONSUMABLE'

export const sortBenefits = (benefits: Types.BenefitAdminDto[] | Types.BenefitClientDto[]) => {
  return [...benefits].sort((a, b) => {
    if (isConsumable(a.type) && !isConsumable(b.type)) {
      return -1
    }
    if (!isConsumable(a.type) && isConsumable(b.type)) {
      return 1
    }
    if (!isConsumable(a.type) && !isConsumable(b.type) && a.amount && b.amount) {
      return a.amount - b.amount
    }
    if (a.qty && b.qty) {
      return a.qty - b.qty
    }
    return 0
  })
}

export const getBenefitOffer = (benefit: Types.BenefitAdminDto | Types.BenefitClientDto, offerId: string) => {
  return benefit.benefitOffers?.find((benefitOffer) => {
    const benefitOfferId = benefitOffer.offer?.id || benefitOffer.offerId
    return benefitOfferId === offerId
  })
}

export const getBenefitOfferLabel = (benefit: Types.BenefitAdminDto, offerId: string) =>
  `${getBenefitOffer(benefit as Types.BenefitAdminDto, offerId || '')?.offer?.displayName || ''} - ${getBenefitDiscount(
    benefit as Types.BenefitAdminDto
  )}`

export const getUniqueBenefitOffers = (benefitConsumptionRecords?: TBenefitConsumptionRecord[]) => {
  const benefitOffers = new Set(
    benefitConsumptionRecords?.map((r) => getBenefitOfferLabel(r.benefit!, r.offerId || ''))
  )
  return Array.from(benefitOffers)
}

export const isUnlimitedBenefitsOnly = (benefits: Types.BenefitAdminDto[] | Types.BenefitClientDto[]) => {
  return benefits.every((benefit) => !isConsumable(benefit.type))
}

export const getMembershipYears = (locationUserProfileMembership: TLocationUserProfileMembership) => {
  const startDate = locationUserProfileMembership?.startDate
  if (!startDate) return []

  const startYear = Number(startDate.substring(0, 4))
  const endYear = new Date().getFullYear() + 1
  return [startYear, endYear]
}

export const getYears = (locationUserProfileMembership: TLocationUserProfileMembership) => {
  const [startYear, endYear] = getMembershipYears(locationUserProfileMembership)
  const years: string[] = []
  for (let year = startYear; year <= endYear; year++) {
    years.push(year.toString())
  }
  return years
}

export const getCycles = (
  locationUserProfileMembership: TLocationUserProfileMembership,
  invoiceFilterDates?: { startDate: string; endDate: string },
  timezone = DEFAULT_TIMEZONE
) => {
  const [startYear, endYear] = getMembershipYears(locationUserProfileMembership)

  const billingCycle = locationUserProfileMembership?.locationMembership?.membership?.billingCycle
  const cycles = new Map<string, TMembershipCycle[]>()

  if (!billingCycle) return cycles

  const cycleDurationInDays = getCycleDurationInDays(billingCycle)
  if (cycleDurationInDays === null) return cycles

  const { startDate, endDate } = invoiceFilterDates || {}
  const invoiceStartDate = startDate ? getTzDateInUtc(startDate, null, timezone) : undefined

  const membershipStartDate = getTzDateInUtc(locationUserProfileMembership?.startDate, null, timezone)

  for (let year = startYear; year <= endYear; year++) {
    const yearCycles: TMembershipCycle[] = []
    const yearStart = getTzDateInUtc(`${year}-01-01`, null, timezone)
    const yearEnd = invoiceFilterDates?.endDate
      ? getTzDateInUtc(endDate, null, timezone)
      : getTzDateInUtc(`${year}-12-31`, null, timezone)

    let currentCycleStartAfter = membershipStartDate
    while (currentCycleStartAfter.getTime() <= yearEnd.getTime()) {
      const cycleEnd = add(currentCycleStartAfter, { days: cycleDurationInDays - 1 })
      if (
        currentCycleStartAfter.getTime() <= yearEnd.getTime() &&
        cycleEnd.getTime() >= yearStart.getTime() &&
        (!invoiceStartDate || currentCycleStartAfter.getTime() >= invoiceStartDate.getTime())
      ) {
        yearCycles.push({
          startDate: currentCycleStartAfter,
          endDate: cycleEnd,
        })
      }

      currentCycleStartAfter = add(currentCycleStartAfter, { days: cycleDurationInDays })
    }

    if (yearCycles.length > 0) {
      cycles.set(
        year.toString(),
        yearCycles.sort((a, b) => b.startDate.getTime() - a.startDate.getTime())
      )
    }
  }
  return cycles
}

export const formatCycleLabel = (cycle?: TMembershipCycle, timezone = DEFAULT_TIMEZONE, format = 'MM/dd/yyyy') => {
  if (!cycle) {
    return ''
  }
  return `${formatInTimeZone(cycle.startDate, timezone, format)} - ${formatInTimeZone(cycle.endDate, timezone, format)}`
}

export const getBCRMembershipCycleForDate = (benefitConsumptionRecord: TBenefitConsumptionRecord) => {
  const lupMembership = benefitConsumptionRecord.locationUserProfileMembership
  const cycle = membershipCycleForDate(
    lupMembership,
    lupMembership?.membership?.billingCycle,
    lupMembership?.membership?.timezone,
    getTzDateInUtc(benefitConsumptionRecord.invoiceStartDate, null, lupMembership?.membership?.timezone)
  )
  if (!cycle) return undefined
  return {
    startDate: cycle.startDate,
    endDate: cycle.endDate,
  }
}

export const getUniqueBenefits = (benefitConsumptionRecords?: TBenefitConsumptionRecord[]): Types.BenefitAdminDto[] => {
  const benefits = new Map()
  benefitConsumptionRecords?.forEach((r) => {
    if (!r.benefit?.id) return
    benefits.set(r.benefit?.id, r.benefit)
  })
  return Array.from(benefits.values()) as Types.BenefitAdminDto[]
}
